var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import FetchProvider from './FetchProvider';
import { NamingService } from './NamingService';
import ConfigurationError, { ConfigurationErrorCode } from './errors/configurationError';
import ResolutionError, { ResolutionErrorCode } from './errors/resolutionError';
import { AccountRecordTypes, } from './types/AccountData';
import { DasSupportedNetwork } from './types/index';
import { ChainId, CoinType, NamingServiceName, } from './types/publicTypes';
export class DasService extends NamingService {
    constructor(source = {}) {
        super();
        this.name = NamingServiceName.DAS;
        if (!(source.url || source.provider)) {
            throw new ConfigurationError(ConfigurationErrorCode.UnspecifiedUrl, {
                method: NamingServiceName.DAS
            });
        }
        if (!source.network) {
            source.network = 'mainnet';
        }
        if (!DasSupportedNetwork.guard(source.network)) {
            throw new ConfigurationError(ConfigurationErrorCode.UnsupportedNetwork, {
                method: NamingServiceName.DAS,
            });
        }
        this.network = source.network;
        this.url = source.url;
        this.provider = source.provider || new FetchProvider(this.name, this.url);
    }
    static autonetwork(source) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!source) {
                source = {
                    url: DasService.UrlMap['mainnet'],
                    network: 'mainnet'
                };
            }
            else if (source.network && !source.url) {
                source.url = DasService.UrlMap[source.network];
            }
            return new DasService(source);
        });
    }
    serviceName() {
        return this.name;
    }
    isSupportedAccount(account) {
        return /.+\.bit/.test(account) && account.split('.').every(v => Boolean(v.length));
    }
    // todo: implement namehash
    namehash(account) {
        if (!this.isSupportedAccount(account)) {
            throw new ResolutionError(ResolutionErrorCode.UnregisteredAccount, {
                account: account,
            });
        }
        return '';
    }
    childhash(_parentHash, label) {
        throw new ResolutionError(ResolutionErrorCode.UnsupportedMethod, {
            account: label,
            methodName: 'childhash'
        });
    }
    owner(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            return accountData.owner_key;
        });
    }
    resolver(account) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new ResolutionError(ResolutionErrorCode.UnsupportedMethod, {
                account: account,
                methodName: 'resolver',
            });
        });
    }
    record(account, key) {
        return __awaiter(this, void 0, void 0, function* () {
            key = key.toLowerCase();
            const returnee = (yield this.allRecords(account))[key];
            if (!returnee) {
                throw new ResolutionError(ResolutionErrorCode.RecordNotFound, {
                    account: account,
                    recordName: key,
                });
            }
            return returnee;
        });
    }
    records(account, keys) {
        return __awaiter(this, void 0, void 0, function* () {
            const records = yield this.allRecords(account);
            return keys.reduce((returnee, key) => {
                returnee[key] = records[key] || '';
                return returnee;
            }, {});
        });
    }
    allRecords(account) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isSupportedAccount(account)) {
                throw new ResolutionError(ResolutionErrorCode.UnsupportedService, {
                    account: account,
                });
            }
            const res = yield this.provider.request({
                method: 'das_accountRecords',
                params: [{
                        account,
                    }]
            });
            const data = res.data;
            if (data) {
                const records = data.records;
                const returnee = {};
                records.forEach(record => {
                    returnee[record.key.toLowerCase()] = record.value;
                });
                return returnee;
            }
            // no records
            else {
                return {};
            }
        });
    }
    reverse(address, currencyTicker) {
        return __awaiter(this, void 0, void 0, function* () {
            const descriptor = DasService.tickerToDescriptorMap[currencyTicker];
            if (!descriptor) {
                throw new Error(`Das currently does not support ${currencyTicker}`);
            }
            const accounts = yield this.getReverseAccount({
                type: descriptor.type,
                key_info: Object.assign(Object.assign({}, descriptor.key_info), { key: address })
            });
            return accounts[0];
        });
    }
    isRegistered(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            return Boolean(accountData);
        });
    }
    twitter(account) {
        throw new ResolutionError(ResolutionErrorCode.UnsupportedMethod, {
            account: account,
            methodName: 'twitter'
        });
    }
    /* -------- custom methods --------- */
    addr(account, ticker) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.record(account, `address.${ticker}`);
        });
    }
    account(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountDataAndRecords(account);
            const profiles = accountData.records.filter(record => record.type === AccountRecordTypes.profile);
            const addresses = accountData.records.filter(record => record.type === AccountRecordTypes.address);
            const dwebs = accountData.records.filter(record => record.type === AccountRecordTypes.dweb);
            const customs = accountData.records.filter(record => record.type === AccountRecordTypes.custom);
            function resolveOneFromRecords(records) {
                return records.reduce((profile, record) => {
                    profile[record.strippedKey] = record;
                    return profile;
                }, {});
            }
            return {
                account: accountData.account,
                avatar: `https://identicons.did.id/identicon/${account}`,
                profile: resolveOneFromRecords(profiles),
                address: resolveOneFromRecords(addresses),
                dweb: resolveOneFromRecords(dwebs),
                custom: resolveOneFromRecords(customs),
                profiles,
                addresses,
                dwebs,
                customs,
                records: accountData.records,
            };
        });
    }
    getReverseAccount(descriptor) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.provider.request({
                method: 'das_reverseRecord',
                params: [descriptor],
            });
            return data.data.account;
        });
    }
    getAccountDataAndRecords(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            accountData.records = yield this.getRecordsData(account);
            return accountData;
        });
    }
    getAccountData(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.provider.request({
                method: 'das_accountInfo',
                params: [{
                        account,
                    }]
            });
            if (!data.data) {
                throw new ResolutionError(ResolutionErrorCode.UnregisteredAccount, {
                    account: account,
                });
            }
            return data.data.account_info;
        });
    }
    getRecordsData(account) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isSupportedAccount(account)) {
                throw new ResolutionError(ResolutionErrorCode.UnsupportedService, {
                    account: account,
                });
            }
            const res = yield this.provider.request({
                method: 'das_accountRecords',
                params: [{
                        account,
                    }]
            });
            const records = res.data.records;
            if (records) {
                records.forEach(record => {
                    // the raw data is string, like '300'
                    record.ttl = Number(record.ttl);
                    const keyParts = record.key.split('.');
                    const type = keyParts.shift();
                    const strippedKey = keyParts.join('.');
                    record.type = type;
                    record.strippedKey = strippedKey;
                });
            }
            return records;
        });
    }
}
DasService.UrlMap = {
    'mainnet': 'https://indexer-not-use-in-production-env.did.id',
    'testnet': 'http://47.243.90.165:8223',
    'aggron': 'http://47.243.90.165:8223',
};
DasService.tickerToDescriptorMap = {
    'ETH': {
        type: 'blockchain',
        key_info: {
            coin_type: CoinType.ETH,
        }
    },
    'BNB': {
        type: 'blockchain',
        key_info: {
            coin_type: CoinType.ETH,
            chain_id: ChainId.BSC
        }
    },
    'TRX': {
        type: 'blockchain',
        key_info: {
            coin_type: CoinType.TRX,
        }
    },
    'MATIC': {
        type: 'blockchain',
        key_info: {
            coin_type: CoinType.MATIC,
            chain_id: ChainId.Polygon
        }
    }
};
//# sourceMappingURL=data:application/json;base64,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